/**
 * GBU type of an audit (HCIT-917): which catalogue applies and how the escalator GBU is shown. The type comes from the
 * audit (`deviceType`, HCIT-911), never from the master data of the unit. Escalator GBUs have no costs, no budget and
 * no ESG (Festlegung 10); their entries carry the chosen measures as ids (`measures`, HCIT-938).
 *
 * Pure functions without store or REST access, so they can be tested without a component.
 */

export const ELEVATOR = "ELEVATOR";
export const ESCALATOR = "ESCALATOR";

export const NO = 2;
export const INADEQUATE = 4;

export const Kind = {
    DEFAULT           : "DEFAULT",
    DEFAULT_INADEQUATE: "DEFAULT_INADEQUATE",
    OPTIONAL          : "OPTIONAL",
};

/** Reports that do not exist for escalators: cost estimation and ESG (audit-svc answers 409, HCIT-918). */
const ELEVATOR_ONLY_REPORTS = [ 112, 115 ];

/** Order in the measure list as in the app: green, red, yellow, then measures without kind. */
const KIND_ORDER = {
    [Kind.DEFAULT]           : 0,
    [Kind.DEFAULT_INADEQUATE]: 1,
    [Kind.OPTIONAL]          : 2,
};

/** Rank of the frequency ids for catalogues without `rank` (audit.T_RA_E_FREQUENCIES.RANK); the ids are not ordered. */
const FREQUENCY_RANK = { 3: 1, 2: 2, 1: 3, 4: 4, 5: 5, 7: 6, 6: 7 };

const kindOrder = function (measure) {
    const order = KIND_ORDER[measure.kind];
    return order === undefined ? 3 : order;
};

const ratingOf = function (entry) {
    return entry && entry.type ? entry.type.id : null;
};

const chosenIds = function (entry) {
    if (!entry) {
        return [];
    }

    if (Array.isArray(entry.measures)) {
        return entry.measures;
    }

    // older audits and elevator entries only have the single measure
    return entry.measure && entry.measure.id ? [ entry.measure.id ] : [];
};

export default {
    /** The GBU type of an audit; audits without type are elevator GBUs (default of audit-svc). */
    deviceType (audit) {
        return audit && audit.deviceType === ESCALATOR ? ESCALATOR : ELEVATOR;
    },

    isEscalator (audit) {
        return this.deviceType(audit) === ESCALATOR;
    },

    /** The catalogue set (`/api/audit/enums`) for the type; the elevator set if the escalator set is missing. */
    enums (elevator, escalator, deviceType) {
        return deviceType === ESCALATOR && escalator ? escalator : elevator;
    },

    /** The code shown for a catalogue entry: G01 for the escalator entry FG01, the id for elevator entries. */
    code (catalogue) {
        return catalogue ? (catalogue.code || catalogue.id) : null;
    },

    /** The risk of the given answer: "Nein" → risk, "Unzulänglich" → riskInadequate, other answers have none. */
    risk (entry) {
        if (!entry || !entry.catalogue) {
            return null;
        }

        switch (ratingOf(entry)) {
            case NO        : return entry.catalogue.risk || null;
            case INADEQUATE: return entry.catalogue.riskInadequate || entry.catalogue.risk || null;
        }

        return null;
    },

    /**
     * The default kind of the answer: red for "Unzulänglich" if the entry has an active red measure, else green.
     */
    defaultKind (entry, rating) {
        const measures = entry && entry.catalogue && entry.catalogue.measures ? entry.catalogue.measures : [];
        if (rating === INADEQUATE && measures.some(m => !m.deleted && m.kind === Kind.DEFAULT_INADEQUATE)) {
            return Kind.DEFAULT_INADEQUATE;
        }

        return Kind.DEFAULT;
    },

    /**
     * The measures of an escalator entry for its answer, as the app offered them (default kind and optional ones,
     * measures without kind always), plus every chosen measure (also deleted ones). Each item carries `chosen`.
     * Answers without measures ("Ja", "nicht zutreffend") get an empty list. Austrian units get the AT text if there
     * is one, as for elevators.
     */
    measures (entry, country) {
        const rating = ratingOf(entry);
        if (rating !== NO && rating !== INADEQUATE) {
            return [];
        }

        const ids = chosenIds(entry);
        const defaultKind = this.defaultKind(entry, rating);
        const catalogue = entry.catalogue && entry.catalogue.measures ? entry.catalogue.measures : [];

        return catalogue
            .filter(measure => measure && measure.id)
            .filter(measure => {
                if (ids.includes(measure.id)) {
                    return true;
                }

                if (measure.deleted) {
                    return false;
                }

                return !measure.kind || KIND_ORDER[measure.kind] === undefined
                    || measure.kind === Kind.OPTIONAL || measure.kind === defaultKind;
            })
            .slice()
            .sort((a, b) => (kindOrder(a) - kindOrder(b)) || (a.id - b.id))
            .map(measure => ({
                id         : measure.id,
                measureId  : measure.measureId,
                description: this.measureText(measure, rating, country),
                kind       : measure.kind || null,
                frequence  : measure.frequence || null,
                weight     : measure.weight || null,
                deleted    : !!measure.deleted,
                chosen     : ids.includes(measure.id),
            }));
    },

    /** The text of a measure: AT text for Austria, the text for "Unzulänglich" if there is one, else the text. */
    measureText (measure, rating, country) {
        if (country === 'AT' && measure.description_at) {
            return measure.description_at;
        }

        return rating === INADEQUATE && measure.descriptionInadequate ? measure.descriptionInadequate : measure.description;
    },

    /**
     * Frequency and severity of an escalator entry: the highest values of the chosen measures (frequency by rank,
     * severity by id 1 klein < 2 kritisch < 3 katastrophal), else the values of the catalogue entry. audit-svc sends
     * them as `effectiveFrequence`/`effectiveWeight`; the disposition data do not, so they are computed here the
     * same way (app HCIT-920, audit-svc EscalatorEntryRating).
     */
    effective (entry, frequencies) {
        if (!entry) {
            return { frequence: null, weight: null };
        }

        const catalogue = entry.catalogue || {};
        const ids = chosenIds(entry);
        const chosen = (catalogue.measures || []).filter(measure => ids.includes(measure.id));

        const rank = value => {
            const item = (frequencies || []).find(f => f.id === value.id);
            if (item && item.rank !== undefined && item.rank !== null) {
                return item.rank;
            }

            const fallback = FREQUENCY_RANK[value.id];
            return fallback === undefined ? 0 : fallback;
        };

        const frequencyValues = chosen.map(measure => measure.frequence).filter(value => value && value.id);
        const weightValues = chosen.map(measure => measure.weight).filter(value => value && value.id);

        const frequence = entry.effectiveFrequence || (frequencyValues.length > 0
            ? frequencyValues.reduce((highest, value) => (rank(value) > rank(highest) ? value : highest))
            : catalogue.frequence || null);

        const weight = entry.effectiveWeight || (weightValues.length > 0
            ? weightValues.reduce((highest, value) => (value.id > highest.id ? value : highest))
            : catalogue.weight || null);

        return { frequence: frequence, weight: weight };
    },

    /**
     * Whether the GBU has the menu items "Externe Sicherheitseinrichtungen" and "Gefahren": elevator GBUs only. The
     * app does not record them for escalators (HCIT-1194).
     */
    hasFacilities (deviceType) {
        return deviceType !== ESCALATOR;
    },

    /** The report menu of the GBU: escalators without cost estimation (112) and ESG (115). */
    reports (deviceType, menu) {
        if (deviceType !== ESCALATOR) {
            return menu;
        }

        return (menu || []).filter(item => !ELEVATOR_ONLY_REPORTS.includes(item.id));
    },

    /**
     * Whether the ESG column is shown: always for elevators; for escalators only while (older) ESG audits of the
     * unit exist, so they stay visible after a type change (HCIT-917 F3).
     */
    showEsg (unitIsEscalator, esgHistory) {
        return !unitIsEscalator || (!!esgHistory && esgHistory.length > 0);
    },

    /**
     * The entry sent by `PUT /api/audit/{id}/entry` when only note and "Maßnahme umgesetzt" of an escalator entry
     * change (HCIT-917 F1): answer and chosen measures are sent unchanged, because audit-svc rewrites the chosen
     * measures from `measures` (else from `measure`) and would otherwise lose them.
     */
    noteUpdate (entry, notice, done) {
        const update = Object.assign({}, entry);
        update.notice = notice;
        update.done = done;
        update.measures = chosenIds(entry).slice();
        return update;
    },

    /**
     * GBU type of a disposition inspection: the type declared by the app (`InspectionDto.deviceType`); inspections of
     * older apps have none, then the entries decide (escalator entries only exist in the escalator catalogue).
     */
    dispositionDeviceType (declared, audit, escalatorCatalogue) {
        if (declared === ESCALATOR || declared === ELEVATOR) {
            return declared;
        }

        const entries = audit && audit.entries ? audit.entries : [];
        const escalator = (escalatorCatalogue || []).some(item => entries.some(entry => entry.id === item.id));
        return escalator ? ESCALATOR : ELEVATOR;
    },

    /**
     * Maps the GBU of a disposition inspection (`InspectionDto.audit`) to the entries of the overview. The catalogue
     * entry is looked up in the set of the inspection's type first and then in the other set, so no entry is dropped
     * (inspections of older apps have no type).
     */
    dispositionEntries (audit, catalogues, ratings) {
        if (!audit || !audit.entries) {
            return [];
        }

        const sets = (catalogues || []).filter(catalogue => !!catalogue);
        const entries = [];
        const seen = new Set();
        sets.forEach(catalogue => {
            catalogue.forEach(item => {
                if (seen.has(item.id)) {
                    return;
                }

                const value = audit.entries.find(v => v.id === item.id);
                if (!value) {
                    return;
                }

                seen.add(item.id);
                entries.push({
                    catalogue: item,
                    type     : (ratings || []).find(r => r.id === value.rating),
                    notice   : value.notes,
                    measure  : value.measure ? {
                        id            : value.measure.id,
                        description   : value.measure.description,
                        description_at: value.measure.description_at
                    } : null,
                    measures : Array.isArray(value.measures) ? value.measures.slice() : null,
                });
            });
        });

        return entries;
    },
}
